import { apiGet } from "@softmax/observatory-client/client";
import type { ApiRequestOptions } from "@softmax/observatory-client/client";
import { ApiError } from "@softmax/observatory-client/transport";
import type { Schemas } from "@softmax/observatory-client/types";
import { limitPublicRead } from "@softmax/web-features/data/public-api";
import { deskFor } from "../../desk-spec";
import { joinPitches } from "./pitch-feed-model";
import { type PitchCursor, type PitchPage } from "../pagination";
import { readPitchSubmissions } from "./pitch-submissions";
import { readPitchEntries } from "./pitch-entries";
import {
  gradedAsSubmissions,
  readGradedEpisodes,
  type GradingRound,
} from "./graded-episodes";

const roundPageSize = 6;

/** Signed-out visitors see graded entries; submissions include entries still being graded. */
export async function readPitchPage({
  leagueId,
  cursor,
  signal,
}: {
  readonly leagueId: string;
  readonly cursor: PitchCursor | null;
  readonly signal?: AbortSignal | undefined;
}): Promise<PitchPage> {
  const options: ApiRequestOptions = { elevatedPrivileges: false };

  if (signal !== undefined) options.signal = signal;

  const get = <T>(path: string) =>
    limitPublicRead(() => apiGet<T>(path, options));
  const league = await get<Schemas["LeaguePublic"]>(
    `/v2/leagues/${encodeURIComponent(leagueId)}`,
  );
  const spec = deskFor(league.game.coworld_name ?? league.game.name);

  if (!spec) throw new ApiError("This game has no desk.", 404);

  const submissionsRead = readPitchSubmissions({ leagueId, cursor, options });

  const roundParams = new URLSearchParams({
    league_id: leagueId,
    status: "completed",
    limit: String(roundPageSize),
  });

  if (cursor?.rounds) roundParams.set("cursor", cursor.rounds);

  const roundRead =
    (!cursor || cursor.rounds) && !cursor?.episodes.length
      ? get<Schemas["RoundListPublic"]>(`/v2/rounds?${roundParams}`)
      : Promise.resolve({ entries: [], next_cursor: cursor?.rounds ?? null });
  const postParams = new URLSearchParams({ sort: "new", limit: "100" });

  if (cursor?.posts) postParams.set("cursor", cursor.posts);

  const postRead: Promise<Schemas["PostFeedPage"]> =
    !cursor || cursor.posts
      ? get<Schemas["PostFeedPage"]>(
          `/v2/forums/${encodeURIComponent(spec.forumSlug)}/posts?${postParams}`,
        ).catch((error: unknown) => {
          if (error instanceof ApiError && error.status === 404)
            return { entries: [], next_cursor: null };

          throw error;
        })
      : Promise.resolve({ entries: [], next_cursor: null });
  const [submissionPage, roundPage, postPage] = await Promise.all([
    submissionsRead,
    roundRead,
    postRead,
  ]);

  const rounds: GradingRound[] = cursor?.episodes.length
    ? cursor.episodes
    : roundPage.entries.map((round) => ({
        roundId: round.id,
        completedAt: round.completed_at ?? round.created_at,
        cursor: null,
      }));
  const { episodes, pendingEpisodes } = await readGradedEpisodes({
    get,
    rounds,
  });
  const submissions = submissionPage.entries;
  const rows = [...submissions, ...gradedAsSubmissions(episodes)];
  const entries = await readPitchEntries({
    get,
    policyVersionIds: rows.map((row) => row.policy_version.id),
  });
  const pitches = joinPitches({
    submissions: rows,
    episodes,
    entries,
    posts: [],
  });
  const nextCursor = {
    rounds: roundPage.next_cursor,
    submissions: submissionPage.next_cursor,
    posts: postPage.next_cursor ?? null,
    episodes: pendingEpisodes,
  };
  return {
    pitches,
    posts: postPage.entries,
    submittedIds: submissions
      .filter(
        (submission) =>
          submission.status !== "withdrawn" && submission.status !== "rejected",
      )
      .map((submission) => submission.policy_version.id),
    observedSubmissionIds: submissions.map(
      (submission) => submission.policy_version.id,
    ),
    signedInRead: submissions.length > 0,
    nextCursor:
      nextCursor.rounds ||
      nextCursor.submissions ||
      nextCursor.posts ||
      nextCursor.episodes.length
        ? nextCursor
        : null,
  };
}
